import {getLayoutData, getPageData, getPages, getPagesWithVariables} from "@/pages/api/api";
import {
	BANNER_CALL_TO_ACTION_QUERY,
	GLOBAL_BASED_BANNER_QUERY,
	SEE_ALSO_INFO_BLOCK_QUERY,
	STATE_QUERY
} from "@/pages/api/fragments";
import useFilterData from "@/hooks/useFIlterData";
import AnnouncementsAndTendersList from "@/components/AnnouncementsAndTendersList";
import useSetBannerData from "@/hooks/useSetBannerData";
import ExtendedContent from "@/components/common/ExtendedContent";
import AnnouncementFiltration from "@/components/common/AnnouncementFiltration";
import {useMemo, useState} from "react";
import SubPageBanner from "@/components/Banners/SubPage/SubPageBanner";
import {CONTENT_TYPES} from "@/constants";
import SeeAlso, {getSeeAlsoData} from "@/components/SeeAlso";
import {getPropSafely} from "@/utils/utils";

export default function Page({ data, singleAnnouncementsAndTenders, announcementsYears }) {
	const slug = getPropSafely(data, 'slug')
	const announcementsAndTenders = useFilterData(singleAnnouncementsAndTenders, false, false)
	const content = getPropSafely(data, 'content', [])
	const bannerData = useSetBannerData(data)

	const [filteredData, setFilteredData] = useState(announcementsAndTenders)

	const seeAlsoInfo = data?.seeAlsoInfo || { header: '', variant: ''}
	const formattedSeeAlsoData = useMemo(() => getSeeAlsoData(data),[data])

	return (
		<>
			<SubPageBanner data={bannerData}/>
			<AnnouncementFiltration
				announcementsYears={announcementsYears}
				initialData={announcementsAndTenders}
				setFilteredData={setFilteredData}
			/>
			<AnnouncementsAndTendersList data={filteredData} parentSlug={slug} />
			<ExtendedContent content={content} />
			<SeeAlso
				header={seeAlsoInfo.header}
				variant={seeAlsoInfo.variant}
				data={formattedSeeAlsoData}
			/>
		</>
	)
}

const TYPE = 'allAnnouncementsAndTendersYears'

const propsQuery = `
	query GetWhatWeDoCategoriesData($slug: String, $locale: SiteLocale)  {
		${TYPE}(locale: $locale, filter: {slug: {eq: $slug }}, first: 500) {
			__typename
			id
            pageName
            slug
			content {
            	__typename
            	${GLOBAL_BASED_BANNER_QUERY} 
            	... on ${CONTENT_TYPES.BANNER_CALL_TO_ACTION_MODULE} {
            		${BANNER_CALL_TO_ACTION_QUERY}
            	}
            }
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
            seeAlsoLinks {
				__typename
				... on ${CONTENT_TYPES.SINGLE_PAGE} {
					__typename
					id
					pageType
					slug
					card {
						header
						description
					}
				}
				... on ${CONTENT_TYPES.ANNOUNCEMENTS_AND_TENDERS_YEARS} {
					__typename
					id
					slug
					year
					card {
						header
						description
					}
				}
		    }
        }
    }
`

const ALL_SINGLE_ANNOUNCEMENTS_AND_TENDERS_PAGES_QUERY = `
	query GetAllAnnouncementsAndTendersPagesData($locale: SiteLocale, $yearID: ItemId) {
		allSingleAnnouncementAndTenderPages(
			locale: $locale,
			filter: {year: { eq: $yearID }}, 
			first: 500
		) {
			__typename
			id
			title
			slug
			year {
				id
				year
			}
			month {
				month
			}
			bannerText
			description
			fromDate
			toDate
			pageType
			pageName
			state { 
				${STATE_QUERY}
			}
		}
	}
`

const YEARS_TYPE = 'allAnnouncementsAndTendersYears'
const yearsQuery = `
	query GetAllAnnouncementsAndTendersYears($locale: SiteLocale) {
		${YEARS_TYPE} (
			locale: $locale, 
			first: 500,
			orderBy: year_DESC,
		){
			id
			year
			slug
	  	}
	}
`;

export async function getServerSideProps({ params, locale }) {
	const { year } = params;
	const layout = await getLayoutData(locale)
	const data = await getPageData(TYPE, year, propsQuery, locale);
	const years = await getPages(YEARS_TYPE,yearsQuery, locale);
	const yearId = years.find(localYear => localYear.year === year)

	const singleAnnouncementsAndTenders = await getPagesWithVariables(
		'allSingleAnnouncementAndTenderPages',
		ALL_SINGLE_ANNOUNCEMENTS_AND_TENDERS_PAGES_QUERY,
		{ locale, yearID: yearId?.id || '' }
	);

	if(!data){
		return { notFound: true }; // Returns a 404 page
	}

	return {
		props: {
			data,
			announcementsYears: years,
			singleAnnouncementsAndTenders,
			locale,
			...layout,
			isDarkSearch: true,
			isAnnouncementsAndTendersYearPage: true,
		},
		// revalidate: 60 * 60
	};
}

// export async function getStaticPaths({ locales }) {
// 	const paths = [];
// 	for (const locale of locales) {
// 		const pages = await getPages(TYPE, pathsQuery, locale);
// 		const newsPage = pages.map((page) => {
// 			return {
// 				params: {
// 					year: page.slug,
// 				},
// 				locale,
// 			};
// 		})
// 			.filter((el) => {
// 				return el !== null;
// 			});
// 		paths.push(...newsPage);
// 	}
// 	return {
// 		paths,
// 		fallback: false,
// 	};
// }
